import type { Ficha as FichaDoContrato } from "../api"
import { Column } from "../componentes/TablePrincipal2"
import { data, moeda, percentual, quantidade, rotulo } from "../formato"
import { MAIUSCULA } from "../lib/classes"
import { BlocoEditavel } from "./Campos"
import { COLUNA_NUMERICA, TabelaDaFicha, VazioDaFicha } from "./Tabela"

type Dotacao = FichaDoContrato["dotacoes"][number]
type Garantia = FichaDoContrato["garantias"][number]
type Item = FichaDoContrato["itens"][number]

const NUMERO = "tabular-nums"
const VALOR = "block text-center tabular-nums"

export function TabelaDosItens({ itens }: { itens: Item[] }) {
  return (
    <TabelaDaFicha<Item>
      dados={itens}
      ordenar={{ ordem: (i) => i.ordem, valor_total: (i) => Number(i.valor_total) }}
      vazio={<VazioDaFicha titulo="Nenhum item cadastrado" />}
    >
      <Column<Item> name="ordem" label="Item" render={(_v, i) => <span className={NUMERO}>{i.ordem}</span>} />
      <Column<Item> name="descricao" label="Descrição" render={(_v, i) => <span className={MAIUSCULA}>{i.descricao}</span>} />
      <Column<Item> name="unidade" label="Unidade" render={(_v, i) => <span className={MAIUSCULA}>{i.unidade}</span>} />
      <Column<Item> name="quantidade" label="Quantidade" render={(_v, i) => <span className={NUMERO}>{quantidade(i.quantidade)}</span>} />
      <Column<Item> name="valor_unitario" label="Unitário" render={(_v, i) => <span className={NUMERO}>{moeda(i.valor_unitario)}</span>} />
      <Column<Item> name="valor_total" label="Total" render={(_v, i) => <span className={NUMERO}>{moeda(i.valor_total)}</span>} />
    </TabelaDaFicha>
  )
}

export function BlocoDotacao({ ficha }: { ficha: FichaDoContrato }) {
  return (
    <BlocoEditavel
      titulo="Dotação"
      editavel={false}
      recado="A classificação orçamentária que banca o contrato."
      leitura={
        <TabelaDaFicha<Dotacao>
          dados={ficha.dotacoes}
          ordenar={{ exercicio: (d) => d.exercicio, valor: (d) => Number(d.valor) }}
          vazio={<VazioDaFicha titulo="Nenhuma dotação vinculada" />}
        >
          <Column<Dotacao> name="exercicio" label="Exercício" render={(_v, d) => <span className={NUMERO}>{d.exercicio}</span>} />
          <Column<Dotacao> name="unidade_orcamentaria" label="Unidade" render={(_v, d) => d.unidade_orcamentaria ?? "—"} />
          <Column<Dotacao> name="funcional_programatica" label="Funcional-programática" render={(_v, d) => d.funcional_programatica ?? "—"} />
          <Column<Dotacao> name="natureza_da_despesa" label="Natureza" render={(_v, d) => d.natureza_da_despesa ?? "—"} />
          <Column<Dotacao> name="fonte_de_recursos" label="Fonte" render={(_v, d) => d.fonte_de_recursos ?? "—"} />
          <Column<Dotacao> name="valor" label="Valor" render={(_v, d) => <span className={NUMERO}>{moeda(d.valor)}</span>} />
        </TabelaDaFicha>
      }
    />
  )
}

export function BlocoGarantias({ ficha }: { ficha: FichaDoContrato }) {
  return (
    <BlocoEditavel
      titulo="Garantias"
      editavel={false}
      recado="A garantia prestada pelo contratado. Ela pode ser exigida, mas não é obrigatória."
      leitura={
        <TabelaDaFicha<Garantia>
          dados={ficha.garantias}
          ordenar={{ vigencia_fim: (g) => g.vigencia_fim, valor: (g) => Number(g.valor) }}
          vazio={<VazioDaFicha titulo="Nenhuma garantia prestada neste contrato" />}
        >
          <Column<Garantia> name="modalidade" label="Modalidade" render={(_v, g) => <span className={MAIUSCULA}>{rotulo(g.modalidade)}</span>} />
          <Column<Garantia> name="percentual" label="%" classe={COLUNA_NUMERICA} render={(_v, g) => <span className={VALOR}>{percentual(g.percentual)}</span>} />
          <Column<Garantia> name="valor" label="Valor" classe={COLUNA_NUMERICA} render={(_v, g) => <span className={VALOR}>{moeda(g.valor)}</span>} />
          <Column<Garantia>
            name="vigencia_fim"
            label="Vigência"
            render={(_v, g) => <span className={NUMERO}>{`${data(g.vigencia_inicio)} a ${data(g.vigencia_fim)}`}</span>}
          />
          <Column<Garantia> name="emissor" label="Emissor" render={(_v, g) => <span className={MAIUSCULA}>{g.emissor ?? "—"}</span>} />
        </TabelaDaFicha>
      }
    />
  )
}
